<template>
  <div class="p-1">
    <!-- Tabs Header -->
    <v-tabs v-model="activeTab" class="mb-4" color="indigo" background-color="transparent">
      <v-tab class="font-bold text-xs uppercase">
        <v-icon left size="16">mdi-email-cog-outline</v-icon>
        Email Setup
      </v-tab>
      <v-tab class="font-bold text-xs uppercase">
        <v-icon left size="16">mdi-history</v-icon>
        Email Logs
      </v-tab>
    </v-tabs>

    <v-tabs-items v-model="activeTab" class="bg-transparent">
      <!-- Tab 1: Email Setup -->
      <v-tab-item>
        <v-card class="rounded-xl border border-slate-100 shadow-sm overflow-hidden" flat>
          <v-card-text class="p-6">
            <div class="flex flex-col gap-6">
              <!-- Title & Description Header -->
              <div class="flex items-start gap-4">
                <v-avatar color="white" class="border border-slate-100 p-1" size="48">
                  <img :src="ImgAws" class="w-10 h-10 object-contain" />
                </v-avatar>
                <div class="flex flex-col">
                  <span class="text-sm font-bold text-slate-800 leading-tight">
                    AWS SES Organization Email Setup
                  </span>
                  <span class="text-xs text-slate-500 mt-1 leading-normal">
                    Configure and verify the official sender email address used for all
                    transactional and automated system emails sent by the organization.
                  </span>
                </div>
              </div>

              <v-divider class="border-slate-100"></v-divider>

              <!-- Status Card -->
              <div v-if="setupLoading" class="py-12 text-center">
                <v-progress-circular indeterminate color="indigo" size="32"></v-progress-circular>
                <div class="text-slate-400 mt-2 font-medium text-xs">
                  Fetching configuration status...
                </div>
              </div>

              <div v-else>
                <!-- Case 1: Active Configuration (Verified or Pending) -->
                <div
                  v-if="
                    emailConfig.configured && emailConfig.emails && emailConfig.emails.length > 0
                  "
                >
                  <div
                    v-for="emailItem in emailConfig.emails"
                    :key="emailItem.email"
                    class="mb-6 p-5 border border-slate-100 rounded-xl bg-white shadow-sm"
                  >
                    <!-- Status Banner -->
                    <div
                      :class="[
                        'rounded-xl p-4 border flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4',
                        emailItem.verified
                          ? 'bg-emerald-50/40 border-emerald-100 text-emerald-950'
                          : 'bg-amber-50/40 border-amber-100 text-amber-950'
                      ]"
                    >
                      <div class="flex items-center gap-3">
                        <v-avatar
                          :color="emailItem.verified ? 'green lighten-5' : 'amber lighten-5'"
                          size="40"
                        >
                          <v-icon :color="emailItem.verified ? 'green darken-2' : 'amber darken-2'">
                            {{
                              emailItem.verified
                                ? 'mdi-check-decagram-outline'
                                : 'mdi-clock-outline'
                            }}
                          </v-icon>
                        </v-avatar>
                        <div class="flex flex-col">
                          <div class="flex items-center gap-2">
                            <span
                              class="text-xs font-semibold text-slate-500 uppercase tracking-wider"
                            >
                              Verification Status
                            </span>
                            <v-chip
                              v-if="emailItem.isDefault"
                              color="indigo"
                              dark
                              class="font-bold text-[9px] uppercase px-2 py-0.5 rounded-full"
                              x-small
                            >
                              Default
                            </v-chip>
                          </div>
                          <span
                            class="text-sm font-bold mt-0.5"
                            :class="emailItem.verified ? 'text-green-700' : 'text-amber-700'"
                          >
                            {{ emailItem.verified ? 'Verified & Active' : 'Verification Pending' }}
                          </span>
                        </div>
                      </div>

                      <div class="flex justify-end items-center gap-2">
                        <v-btn
                          v-if="!emailItem.verified"
                          color="amber darken-3"
                          outlined
                          class="normal-case rounded-lg text-xs"
                          small
                          @click="resendVerification(emailItem.email)"
                          :loading="setupLoading"
                        >
                          <v-icon left size="14">mdi-email-send-outline</v-icon>
                          Resend Mail
                        </v-btn>
                        <v-btn
                          color="indigo"
                          outlined
                          class="normal-case rounded-lg text-xs"
                          small
                          @click="fetchEmailSetupStatus"
                          :loading="setupLoading"
                        >
                          <v-icon left size="14">mdi-refresh</v-icon>
                          Check Status
                        </v-btn>
                      </div>
                    </div>

                    <!-- Configuration Details -->
                    <div class="bg-slate-50/50 rounded-xl border border-slate-100 p-4 mb-4">
                      <div class="grid grid-cols-1 md:grid-cols-2 gap-y-3 gap-x-6 text-xs">
                        <div class="flex flex-col gap-1">
                          <span class="font-semibold text-slate-400">Configured Email Address</span>
                          <span class="text-sm font-bold text-slate-800">{{
                            emailItem.email
                          }}</span>
                        </div>
                        <div class="flex flex-col gap-1">
                          <span class="font-semibold text-slate-400">AWS Identity Type</span>
                          <span class="text-slate-700 font-semibold">Email Address</span>
                        </div>
                        <div class="flex flex-col gap-1" v-if="emailItem.verificationRequestedAt">
                          <span class="font-semibold text-slate-400"
                            >Requested Verification At</span
                          >
                          <span class="text-slate-700 font-medium">
                            {{ formatDateTime(emailItem.verificationRequestedAt) }}
                          </span>
                        </div>
                        <div
                          class="flex flex-col gap-1"
                          v-if="emailItem.verified && emailItem.verifiedAt"
                        >
                          <span class="font-semibold text-slate-400">Verified At</span>
                          <span class="text-slate-700 font-medium">
                            {{ formatDateTime(emailItem.verifiedAt) }}
                          </span>
                        </div>
                      </div>
                    </div>

                    <!-- Pending Status Alert Message -->
                    <v-alert
                      v-if="!emailItem.verified"
                      type="info"
                      outlined
                      prominent
                      border="left"
                      class="rounded-xl text-xs mb-4"
                      color="indigo"
                      icon="mdi-information-outline"
                    >
                      A verification link was sent to <strong>{{ emailItem.email }}</strong
                      >. Please check the spam folder if you do not find the email in the inbox.
                      After clicking the AWS verification link, click the
                      <strong>Check Status</strong> button above to activate the integration.
                    </v-alert>

                    <!-- Management & Controls -->
                    <div class="flex justify-end gap-2">
                      <v-btn
                        v-if="emailItem.verified && !emailItem.isDefault"
                        color="indigo"
                        class="normal-case rounded-lg text-xs white--text"
                        small
                        depressed
                        @click="setDefaultEmail(emailItem.email)"
                        :loading="setupLoading"
                      >
                        <v-icon left size="14">mdi-check-circle-outline</v-icon>
                        Set as Default
                      </v-btn>
                      <v-btn
                        color="red"
                        outlined
                        class="normal-case rounded-lg text-xs"
                        small
                        @click="removeEmail(emailItem.email)"
                        :loading="setupLoading"
                      >
                        <v-icon left size="14">mdi-delete-outline</v-icon>
                        Remove Email
                      </v-btn>
                    </div>
                  </div>
                </div>

                <!-- Case 2: Register Sender Email Address (Visible if < 2 configured) -->
                <div
                  v-if="
                    !emailConfig.configured || (emailConfig.emails && emailConfig.emails.length < 2)
                  "
                >
                  <v-alert
                    type="warning"
                    outlined
                    class="rounded-xl text-xs mb-6"
                    color="amber darken-3"
                    icon="mdi-alert-circle-outline"
                    v-if="!emailConfig.configured"
                  >
                    There is currently no sender email configured for this organization. You must
                    configure one to enable system-generated email alerts and communication.
                  </v-alert>

                  <v-card class="border border-slate-100 rounded-xl bg-slate-50/50" flat>
                    <v-card-text class="p-5 flex flex-col gap-4">
                      <div class="text-xs font-bold text-slate-800">
                        Register Sender Email Address
                      </div>

                      <div class="flex flex-col md:flex-row items-start gap-4">
                        <div class="flex-grow w-full md:max-w-md">
                          <v-text-field
                            v-model="setupEmail"
                            placeholder="e.g. contact@myorganization.com"
                            outlined
                            dense
                            hide-details="auto"
                            prepend-inner-icon="mdi-email-outline"
                            class="rounded-lg bg-white text-xs"
                            @input="checkEmailAvailability"
                            :loading="emailChecking"
                            :error="!emailAvailable && setupEmail.length > 0"
                            :error-messages="
                              !emailAvailable && setupEmail.length > 0
                                ? [emailAvailabilityMessage]
                                : []
                            "
                          ></v-text-field>
                          <div
                            v-if="
                              emailAvailable &&
                              setupEmail.length > 0 &&
                              validateEmailFormat(setupEmail)
                            "
                            class="text-[11px] text-emerald-600 mt-1 font-semibold flex items-center gap-1"
                          >
                            <v-icon color="emerald" size="12">mdi-check-circle-outline</v-icon>
                            Email address is available for use.
                          </div>
                        </div>

                        <div class="flex items-center gap-2">
                          <v-btn
                            color="indigo"
                            class="normal-case rounded-lg text-xs white--text"
                            depressed
                            @click="registerEmail()"
                            :loading="setupLoading"
                            :disabled="
                              !setupEmail ||
                              !validateEmailFormat(setupEmail) ||
                              !emailAvailable ||
                              emailChecking
                            "
                          >
                            <v-icon left size="16">mdi-email-plus-outline</v-icon>
                            Verify & Register
                          </v-btn>
                        </div>
                      </div>

                      <div class="text-[11px] text-slate-500 leading-normal">
                        <strong>Note:</strong> AWS SES requires verification for any sender email. A
                        verification email will be sent to this address by AWS SES. The status will
                        remain pending until the email verification link is clicked. You can add up
                        to a maximum of two sender emails.
                      </div>
                    </v-card-text>
                  </v-card>
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-tab-item>

      <!-- Tab 2: Email Logs -->
      <v-tab-item>
        <!-- Filters & Actions Header -->
        <v-card class="mb-4 rounded-xl border border-slate-100 shadow-sm" flat>
          <v-card-text class="p-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <!-- Left: Search and Filters -->
            <div class="flex flex-wrap items-center gap-3 flex-grow max-w-4xl">
              <v-text-field
                v-model="search"
                placeholder="Search by Email, Subject, Message ID or Template Name..."
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-magnify"
                class="rounded-lg max-w-sm bg-slate-50 text-xs"
                @keyup.enter="fetchLogs"
                clearable
                @click:clear="clearSearch"
              ></v-text-field>

              <v-select
                v-model="selectedEventType"
                :items="eventTypeOptions"
                label="Event Type"
                outlined
                dense
                hide-details
                class="rounded-lg max-w-[200px] bg-slate-50 text-xs"
                @change="fetchLogs"
              ></v-select>
            </div>

            <!-- Right: Actions -->
            <div class="flex justify-end items-center gap-2">
              <v-btn
                color="indigo"
                class="normal-case rounded-lg text-xs white--text"
                depressed
                @click="exportToExcel"
                :loading="exporting"
                :disabled="!logs.length"
              >
                <v-icon left size="16">mdi-file-excel-outline</v-icon>
                Export Excel
              </v-btn>
              <v-btn
                color="indigo"
                outlined
                class="normal-case rounded-lg text-xs"
                @click="fetchLogs"
                :loading="loading"
              >
                <v-icon left size="16">mdi-refresh</v-icon>
                Refresh
              </v-btn>
            </div>
          </v-card-text>
        </v-card>

        <!-- Table Card -->
        <v-card class="rounded-xl border border-slate-100 shadow-sm overflow-hidden" flat>
          <v-simple-table class="text-xs">
            <template v-slot:default>
              <thead>
                <tr
                  class="bg-slate-50/75 border-b border-slate-100 text-slate-500 font-semibold text-[11px] uppercase tracking-wider"
                >
                  <th class="py-3 px-4 text-left">Message ID</th>
                  <th class="py-3 px-4 text-left">Sender (Source)</th>
                  <th class="py-3 px-4 text-left">Recipient (Destination)</th>
                  <th class="py-3 px-4 text-left">Subject</th>
                  <th class="py-3 px-4 text-center">Event Type</th>
                  <th class="py-3 px-4 text-left">Timestamp</th>
                  <th class="py-3 px-4 text-center">Actions</th>
                </tr>
              </thead>
              <tbody v-if="!loading && logs.length">
                <tr
                  v-for="log in logs"
                  :key="log._id"
                  class="border-b border-slate-100 hover:bg-slate-50/50 transition-colors"
                >
                  <!-- Message ID -->
                  <td class="py-3 px-4 font-mono text-[10px] text-slate-500 max-w-[150px] truncate">
                    {{ log.messageId }}
                  </td>

                  <!-- Sender -->
                  <td class="py-3 px-4 text-slate-600 font-medium">
                    {{ log.source }}
                  </td>

                  <!-- Recipient -->
                  <td class="py-3 px-4 text-slate-600 font-medium max-w-[200px] truncate">
                    {{ formatDestination(log.destination) }}
                  </td>

                  <!-- Subject -->
                  <td class="py-3 px-4 text-slate-700 font-medium max-w-[220px] truncate">
                    {{ log.subject || 'No Subject' }}
                  </td>

                  <!-- Event Type Badge -->
                  <td class="py-3 px-4 text-center">
                    <v-chip
                      :color="getBadgeColor(log.eventType)"
                      class="white--text font-bold text-[9px] uppercase px-2.5 py-0.5 rounded-full"
                      small
                    >
                      {{ log.eventType }}
                    </v-chip>
                  </td>

                  <!-- Timestamp -->
                  <td class="py-3 px-4 text-slate-500">
                    {{ formatDateTime(log.timestamp) }}
                  </td>

                  <!-- Actions -->
                  <td class="py-3 px-4 text-center">
                    <v-btn
                      icon
                      color="indigo"
                      small
                      @click="openDetails(log)"
                      class="hover:bg-indigo-50 rounded-lg"
                    >
                      <v-icon size="16">mdi-eye-outline</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </tbody>
              <tbody v-else-if="loading">
                <tr>
                  <td colspan="7" class="py-8 text-center">
                    <v-progress-circular
                      indeterminate
                      color="indigo"
                      size="24"
                    ></v-progress-circular>
                    <div class="text-slate-400 mt-2 font-medium">Loading SES Email Logs...</div>
                  </td>
                </tr>
              </tbody>
              <tbody v-else>
                <tr>
                  <td colspan="7" class="py-12 text-center text-slate-400 font-medium">
                    <v-icon size="40" class="mb-2 text-slate-300">mdi-email-alert-outline</v-icon>
                    <div>No SES Webhook logs found</div>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>

          <!-- Pagination Footer -->
          <div
            v-if="logs.length"
            class="p-4 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white"
          >
            <div class="text-xs font-semibold text-slate-500">
              Showing {{ (page - 1) * limit + 1 }} to {{ Math.min(page * limit, totalRecords) }} of
              {{ totalRecords }} records
            </div>
            <div class="flex items-center gap-4">
              <v-select
                v-model="limit"
                :items="[10, 20, 50, 100]"
                outlined
                dense
                hide-details
                class="rounded-lg max-w-[80px] text-xs bg-slate-50"
                @change="changeLimit"
              ></v-select>
              <div class="flex items-center gap-1">
                <v-btn
                  icon
                  small
                  class="border border-slate-200 rounded-lg hover:bg-slate-50"
                  :disabled="page === 1"
                  @click="prevPage"
                >
                  <v-icon size="16">mdi-chevron-left</v-icon>
                </v-btn>
                <span class="text-xs font-bold text-slate-600 px-2"
                  >Page {{ page }} of {{ totalPages }}</span
                >
                <v-btn
                  icon
                  small
                  class="border border-slate-200 rounded-lg hover:bg-slate-50"
                  :disabled="page === totalPages"
                  @click="nextPage"
                >
                  <v-icon size="16">mdi-chevron-right</v-icon>
                </v-btn>
              </div>
            </div>
          </div>
        </v-card>
      </v-tab-item>
    </v-tabs-items>

    <!-- Details Dialog -->
    <v-dialog v-model="detailsDialog" max-width="650px" scrollable>
      <v-card class="rounded-2xl border border-slate-100 shadow-xl bg-slate-50 flex flex-col p-4">
        <!-- Dialog Header -->
        <v-card-title
          class="bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between rounded-t-xl"
        >
          <div class="flex items-center gap-3">
            <v-avatar color="white" class="border border-slate-100 p-1" size="40">
              <img :src="ImgAws" class="w-8 h-8 object-contain" />
            </v-avatar>
            <div class="flex flex-col">
              <span class="text-sm font-bold text-slate-800 leading-tight">
                SES Webhook Details
              </span>
              <span
                class="text-[10px] text-slate-400 font-semibold uppercase tracking-wider mt-0.5"
              >
                {{ activeLog.messageId }}
              </span>
            </div>
          </div>
          <v-btn
            icon
            color="slate"
            @click="detailsDialog = false"
            class="hover:bg-slate-100 rounded-lg"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <!-- Dialog Content -->
        <v-card-text class="flex-grow p-6 overflow-y-auto flex flex-col gap-5">
          <!-- Overview Cards -->
          <div class="grid grid-cols-2 gap-3">
            <div class="bg-white p-3 rounded-xl border border-slate-100 flex flex-col gap-0.5">
              <span class="text-[9px] uppercase font-bold text-slate-400 tracking-wider"
                >Event Type</span
              >
              <div class="flex items-center mt-1">
                <v-chip
                  :color="getBadgeColor(activeLog.eventType)"
                  class="white--text font-bold text-[10px] uppercase"
                  x-small
                >
                  {{ activeLog.eventType }}
                </v-chip>
              </div>
            </div>
            <div class="bg-white p-3 rounded-xl border border-slate-100 flex flex-col gap-0.5">
              <span class="text-[9px] uppercase font-bold text-slate-400 tracking-wider"
                >Received At</span
              >
              <span class="text-xs font-semibold text-slate-700 mt-1">
                {{ formatDateTime(activeLog.timestamp) }}
              </span>
            </div>
          </div>

          <!-- Email Info Card -->
          <div class="bg-white p-4 rounded-xl border border-slate-100 flex flex-col gap-3">
            <h4 class="text-xs font-bold text-slate-800 border-b border-slate-50 pb-2">
              Email Headers
            </h4>
            <div class="grid grid-cols-1 gap-2.5 text-xs">
              <div class="flex items-start">
                <span class="font-semibold text-slate-400 w-24 flex-shrink-0">From:</span>
                <span class="text-slate-700 font-medium">{{ activeLog.source }}</span>
              </div>
              <div class="flex items-start">
                <span class="font-semibold text-slate-400 w-24 flex-shrink-0">To:</span>
                <span class="text-slate-700 font-medium">{{
                  formatDestination(activeLog.destination)
                }}</span>
              </div>
              <div class="flex items-start">
                <span class="font-semibold text-slate-400 w-24 flex-shrink-0">Subject:</span>
                <span class="text-slate-700 font-medium">{{
                  activeLog.subject || 'No Subject'
                }}</span>
              </div>
            </div>
          </div>

          <!-- Event Specific Info Cards -->
          <!-- Bounce Info -->
          <div
            v-if="activeLog.eventType === 'Bounce'"
            class="bg-red-50/50 border border-red-100 p-4 rounded-xl flex flex-col gap-3"
          >
            <h4
              class="text-xs font-bold text-red-900 border-b border-red-100/50 pb-2 flex items-center gap-1.5"
            >
              <v-icon color="red" size="14">mdi-alert-circle</v-icon> Bounce Information
            </h4>
            <div class="grid grid-cols-1 gap-2 text-xs">
              <div class="flex">
                <span class="font-semibold text-slate-500 w-32">Bounce Type:</span>
                <span class="text-slate-700 font-semibold">{{ activeLog.bounceType }}</span>
              </div>
              <div class="flex">
                <span class="font-semibold text-slate-500 w-32">Bounce Sub Type:</span>
                <span class="text-slate-700 font-semibold">{{ activeLog.bounceSubType }}</span>
              </div>
              <div
                v-if="activeLog.bouncedRecipients && activeLog.bouncedRecipients.length"
                class="flex flex-col gap-1 mt-1"
              >
                <span class="font-semibold text-slate-500">Bounced Recipients:</span>
                <div
                  class="bg-white p-2 rounded border border-red-100 text-[11px] font-mono text-slate-600"
                >
                  <div v-for="(rec, i) in activeLog.bouncedRecipients" :key="i" class="mb-1">
                    • <strong>{{ rec.emailAddress }}</strong
                    >: {{ rec.diagnosticCode || 'No diagnostic code' }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Open Info -->
          <div
            v-if="activeLog.eventType === 'Open'"
            class="bg-teal-50/50 border border-teal-100 p-4 rounded-xl flex flex-col gap-3"
          >
            <h4
              class="text-xs font-bold text-teal-900 border-b border-teal-100/50 pb-2 flex items-center gap-1.5"
            >
              <v-icon color="teal" size="14">mdi-email-open-outline</v-icon> Open Information
            </h4>
            <div class="grid grid-cols-1 gap-2 text-xs">
              <div class="flex">
                <span class="font-semibold text-slate-500 w-32">Opened At:</span>
                <span class="text-slate-700 font-semibold">{{
                  formatDateTime(activeLog.openTimestamp || activeLog.timestamp)
                }}</span>
              </div>
              <div v-if="activeLog.userAgent" class="flex flex-col gap-1">
                <span class="font-semibold text-slate-500">User Agent:</span>
                <span
                  class="text-slate-600 bg-white p-2 rounded border border-teal-100 font-mono text-[10px] break-all leading-normal"
                >
                  {{ activeLog.userAgent }}
                </span>
              </div>
            </div>
          </div>

          <!-- Click Info -->
          <div
            v-if="activeLog.eventType === 'Click'"
            class="bg-indigo-50/50 border border-indigo-100 p-4 rounded-xl flex flex-col gap-3"
          >
            <h4
              class="text-xs font-bold text-indigo-900 border-b border-indigo-100/50 pb-2 flex items-center gap-1.5"
            >
              <v-icon color="indigo" size="14">mdi-cursor-default-click-outline</v-icon> Click
              Information
            </h4>
            <div class="grid grid-cols-1 gap-2 text-xs">
              <div class="flex">
                <span class="font-semibold text-slate-500 w-32">Clicked At:</span>
                <span class="text-slate-700 font-semibold">{{
                  formatDateTime(activeLog.clickTimestamp || activeLog.timestamp)
                }}</span>
              </div>
              <div v-if="activeLog.link" class="flex flex-col gap-1">
                <span class="font-semibold text-slate-500">Clicked Link:</span>
                <a
                  :href="activeLog.link"
                  target="_blank"
                  class="text-indigo-600 bg-white p-2.5 rounded border border-indigo-100 font-mono text-[10px] break-all hover:underline"
                >
                  {{ activeLog.link }}
                </a>
              </div>
            </div>
          </div>

          <!-- Complaint Info -->
          <div
            v-if="activeLog.eventType === 'Complaint'"
            class="bg-rose-50/50 border border-rose-100 p-4 rounded-xl flex flex-col gap-3"
          >
            <h4
              class="text-xs font-bold text-rose-900 border-b border-rose-100/50 pb-2 flex items-center gap-1.5"
            >
              <v-icon color="rose" size="14">mdi-alert-octagon</v-icon> Complaint Information
            </h4>
            <div class="grid grid-cols-1 gap-2 text-xs">
              <div class="flex">
                <span class="font-semibold text-slate-500 w-32">Feedback Type:</span>
                <span class="text-slate-700 font-semibold">{{
                  activeLog.complaintFeedbackType || 'spam'
                }}</span>
              </div>
              <div
                v-if="activeLog.complainedRecipients && activeLog.complainedRecipients.length"
                class="flex flex-col gap-1"
              >
                <span class="font-semibold text-slate-500">Complained Recipients:</span>
                <div
                  class="bg-white p-2 rounded border border-rose-100 text-[11px] font-mono text-slate-600"
                >
                  <div v-for="(rec, i) in activeLog.complainedRecipients" :key="i">
                    • <strong>{{ rec.emailAddress }}</strong>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Reject / Delivery Performance Info -->
          <div
            v-if="activeLog.eventType === 'Reject'"
            class="bg-amber-50/50 border border-amber-100 p-4 rounded-xl flex flex-col gap-3"
          >
            <h4
              class="text-xs font-bold text-amber-900 border-b border-amber-100/50 pb-2 flex items-center gap-1.5"
            >
              <v-icon color="amber darken-3" size="14">mdi-block-helper</v-icon> Reject Reason
            </h4>
            <span class="text-slate-700 font-semibold text-xs">{{
              activeLog.reason || 'Rejected by AWS SES policies.'
            }}</span>
          </div>

          <div
            v-if="activeLog.eventType === 'Delivery'"
            class="bg-emerald-50/50 border border-emerald-100 p-4 rounded-xl flex flex-col gap-3"
          >
            <h4
              class="text-xs font-bold text-emerald-900 border-b border-emerald-100/50 pb-2 flex items-center gap-1.5"
            >
              <v-icon color="emerald" size="14">mdi-checkbox-marked-circle-outline</v-icon> Delivery
              Performance
            </h4>
            <div class="grid grid-cols-1 gap-2 text-xs">
              <div class="flex">
                <span class="font-semibold text-slate-500 w-44">Delivery Time:</span>
                <span class="text-slate-700 font-semibold">{{
                  formatDateTime(activeLog.deliveryTimestamp || activeLog.timestamp)
                }}</span>
              </div>
              <div v-if="activeLog.processingTimeMillis" class="flex">
                <span class="font-semibold text-slate-500 w-44">Processing Time:</span>
                <span class="text-slate-700 font-semibold"
                  >{{ activeLog.processingTimeMillis }} ms</span
                >
              </div>
            </div>
          </div>
        </v-card-text>

        <!-- Dialog Footer -->
        <v-card-actions class="bg-white border-t border-slate-100 p-4 justify-end rounded-b-xl">
          <v-btn
            color="indigo"
            class="normal-case rounded-lg text-xs"
            dark
            @click="detailsDialog = false"
          >
            Close View
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { debounce } from '@/utils/common'
import ImgAws from '@/assets/img/aws_logo.png'
import * as XLSX from 'xlsx'

export default {
  name: 'AwsMailIntegration',

  data() {
    return {
      ImgAws,
      activeTab: 0,
      exporting: false,

      // Email Setup state
      setupLoading: false,
      setupEmail: '',
      isEditMode: false,
      emailChecking: false,
      emailAvailable: true,
      emailAvailabilityMessage: '',
      emailConfig: {
        configured: false,
        emails: []
      },

      // Email Logs state
      logs: [],
      loading: false,
      search: '',
      selectedEventType: '',

      page: 1,
      limit: 20,
      totalRecords: 0,

      detailsDialog: false,
      activeLog: {},

      eventTypeOptions: [
        { text: 'All Events', value: '' },
        { text: 'Send', value: 'Send' },
        { text: 'Delivery', value: 'Delivery' },
        { text: 'Open', value: 'Open' },
        { text: 'Click', value: 'Click' },
        { text: 'Bounce', value: 'Bounce' },
        { text: 'Complaint', value: 'Complaint' },
        { text: 'Reject', value: 'Reject' }
      ]
    }
  },

  computed: {
    totalPages() {
      return Math.ceil(this.totalRecords / this.limit) || 1
    }
  },

  watch: {
    search() {
      this.page = 1
      this.debouncedFetchLogs()
    }
  },

  created() {
    this.debouncedFetchLogs = debounce(this.fetchLogs, 500)
  },

  mounted() {
    this.fetchEmailSetupStatus()
    this.fetchLogs()
  },

  methods: {
    async fetchEmailSetupStatus() {
      this.setupLoading = true
      try {
        const response = await this.$axios.get('/orgSetup/org-email-management/status')
        if (response.data && response.data.success) {
          this.emailConfig = {
            configured: response.data.configured,
            emails: response.data.emails || []
          }
          this.setupEmail = ''
          this.isEditMode = false
        }
      } catch (error) {
        console.error('Failed to fetch AWS SES email status:', error)
        this.$toast.error('Failed to load email integration status')
      } finally {
        this.setupLoading = false
      }
    },

    async checkEmailAvailability() {
      if (!this.setupEmail || !this.validateEmailFormat(this.setupEmail)) {
        this.emailAvailable = false
        this.emailAvailabilityMessage = 'Please enter a valid email address'
        return
      }

      this.emailChecking = true
      try {
        const response = await this.$axios.get(
          '/orgSetup/org-email-management/check-availability',
          {
            params: { email: this.setupEmail }
          }
        )
        if (response.data && response.data.success) {
          this.emailAvailable = response.data.available
          this.emailAvailabilityMessage = response.data.message
        }
      } catch (error) {
        console.error('Failed to check email availability:', error)
        this.emailAvailable = false
        this.emailAvailabilityMessage = 'Failed to verify email availability'
      } finally {
        this.emailChecking = false
      }
    },

    validateEmailFormat(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return emailRegex.test(email)
    },

    async registerEmail() {
      if (!this.setupEmail) {
        this.$toast.error('Email is required')
        return
      }
      if (!this.validateEmailFormat(this.setupEmail)) {
        this.$toast.error('Invalid email format')
        return
      }

      this.setupLoading = true
      try {
        const response = await this.$axios.post('/orgSetup/org-email-management/onboard', {
          email: this.setupEmail
        })
        if (response.data && response.data.success) {
          this.$toast.success(response.data.message || 'Verification email sent successfully')
          await this.fetchEmailSetupStatus()
        }
      } catch (error) {
        console.error('Failed to onboard email:', error)
        const msg = error.response?.data?.message || 'Failed to onboard email'
        this.$toast.error(msg)
      } finally {
        this.setupLoading = false
      }
    },

    async resendVerification(email) {
      if (!email) return
      this.setupLoading = true
      try {
        const response = await this.$axios.post(
          '/orgSetup/org-email-management/resend-verification',
          {
            email
          }
        )
        if (response.data && response.data.success) {
          this.$toast.success(response.data.message || 'Verification email resent successfully')
          await this.fetchEmailSetupStatus()
        }
      } catch (error) {
        console.error('Failed to resend verification email:', error)
        const msg = error.response?.data?.message || 'Failed to resend verification email'
        this.$toast.error(msg)
      } finally {
        this.setupLoading = false
      }
    },

    async removeEmail(email) {
      if (!email) return
      if (!confirm(`Are you sure you want to remove the sender email "${email}"?`)) {
        return
      }

      this.setupLoading = true
      try {
        const response = await this.$axios.delete('/orgSetup/org-email-management/remove', {
          params: { email }
        })
        if (response.data && response.data.success) {
          this.$toast.success(response.data.message || 'Email removed successfully')
          await this.fetchEmailSetupStatus()
        }
      } catch (error) {
        console.error('Failed to remove email:', error)
        const msg = error.response?.data?.message || 'Failed to remove email'
        this.$toast.error(msg)
      } finally {
        this.setupLoading = false
      }
    },

    async setDefaultEmail(email) {
      if (!email) return
      this.setupLoading = true
      try {
        const updatedEmails = this.emailConfig.emails.map((e) => ({
          email: e.email,
          verificationStatus: e.status,
          verificationRequestedAt: e.verificationRequestedAt,
          verifiedAt: e.verifiedAt,
          isDefault: e.email === email
        }))

        const payload = {
          emailConfig: {
            senderEmails: updatedEmails
          }
        }

        const response = await this.$axios.post('/org/config', payload)
        if (response.data && response.data.success) {
          this.$toast.success('Default email updated successfully')
          await this.fetchEmailSetupStatus()
        }
      } catch (error) {
        console.error('Failed to set default email:', error)
        const msg = error.response?.data?.message || 'Failed to set default email'
        this.$toast.error(msg)
      } finally {
        this.setupLoading = false
      }
    },

    async fetchLogs() {
      this.loading = true
      try {
        const params = {
          page: this.page,
          limit: this.limit
        }

        if (this.selectedEventType) {
          params.eventType = this.selectedEventType
        }

        if (this.search && this.search.trim()) {
          params.search = this.search.trim()
        }

        const response = await this.$axios.get('/sesLogs', { params })
        if (response.data && response.data.success) {
          this.logs = response.data.data
          this.totalRecords = response.data.pagination.total
        }
      } catch (error) {
        console.error('Failed to fetch AWS SES Logs:', error)
        this.$toast.error('Failed to load SES email logs')
      } finally {
        this.loading = false
      }
    },

    clearSearch() {
      this.search = ''
    },

    changeLimit() {
      this.page = 1
      this.fetchLogs()
    },

    prevPage() {
      if (this.page > 1) {
        this.page--
        this.fetchLogs()
      }
    },

    nextPage() {
      if (this.page < this.totalPages) {
        this.page++
        this.fetchLogs()
      }
    },

    formatDestination(dest) {
      if (!dest) return 'N/A'
      if (Array.isArray(dest)) {
        return dest.join(', ')
      }
      return dest
    },

    formatDateTime(dateString) {
      if (!dateString) return ''
      return new Date(dateString).toLocaleString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    },

    getBadgeColor(type) {
      switch (type) {
        case 'Send':
          return 'blue'
        case 'Delivery':
          return 'emerald'
        case 'Open':
          return 'teal'
        case 'Click':
          return 'indigo'
        case 'Bounce':
          return 'orange darken-2'
        case 'Complaint':
        case 'Reject':
          return 'red darken-2'
        default:
          return 'grey'
      }
    },

    openDetails(log) {
      this.activeLog = log
      this.detailsDialog = true
    },

    async exportToExcel() {
      this.exporting = true
      try {
        const params = {
          page: 1,
          limit: 10000
        }

        if (this.selectedEventType) {
          params.eventType = this.selectedEventType
        }

        if (this.search && this.search.trim()) {
          params.search = this.search.trim()
        }

        const response = await this.$axios.get('/sesLogs', { params })
        if (response.data && response.data.success) {
          const logsToExport = response.data.data || []
          if (!logsToExport.length) {
            this.$toast.warning('No logs found to export')
            return
          }

          const extractNameFromEmail = (email) => {
            if (!email) return 'Recipient'
            const username = email.split('@')[0]
            const namePart = username.replace(/[._-]/g, ' ')
            return namePart.replace(/\b\w/g, (c) => c.toUpperCase())
          }

          const exportRows = []
          const seenEmails = new Set()
          logsToExport.forEach((log) => {
            const destinations = Array.isArray(log.destination)
              ? log.destination
              : [log.destination]
            destinations.forEach((dest) => {
              if (dest) {
                const normalizedEmail = dest.trim().toLowerCase()
                if (!seenEmails.has(normalizedEmail)) {
                  seenEmails.add(normalizedEmail)
                  exportRows.push({
                    Name: extractNameFromEmail(dest),
                    Email: dest,
                    'Message ID': log.messageId || '',
                    Subject: log.subject || 'No Subject',
                    'Event Type': log.eventType || '',
                    Timestamp: this.formatDateTime(log.timestamp)
                  })
                }
              }
            })
          })

          const worksheet = XLSX.utils.json_to_sheet(exportRows)
          const workbook = XLSX.utils.book_new()
          XLSX.utils.book_append_sheet(workbook, worksheet, 'Email Logs')
          XLSX.writeFile(workbook, 'AWS_SES_Email_Logs.xlsx')
          this.$toast.success('Logs exported successfully')
        }
      } catch (error) {
        console.error('Failed to export logs:', error)
        this.$toast.error('Failed to export email logs')
      } finally {
        this.exporting = false
      }
    }
  }
}
</script>

<style scoped>
.text-gray-600 {
  color: #4b5563;
}
.bg-slate-50\/75 {
  background-color: rgba(248, 250, 252, 0.75);
}
.bg-slate-50\/50 {
  background-color: rgba(248, 250, 252, 0.5);
}
.bg-red-50\/50 {
  background-color: rgba(254, 242, 242, 0.5);
}
.bg-teal-50\/50 {
  background-color: rgba(240, 253, 250, 0.5);
}
.bg-indigo-50\/50 {
  background-color: rgba(238, 242, 255, 0.5);
}
.bg-rose-50\/50 {
  background-color: rgba(255, 241, 242, 0.5);
}
.bg-amber-50\/50 {
  background-color: rgba(254, 243, 199, 0.5);
}
.bg-emerald-50\/50 {
  background-color: rgba(236, 253, 245, 0.5);
}
.v-simple-table >>> th {
  font-weight: 600 !important;
  color: #64748b !important;
  border-bottom: 1px solid #e2e8f0 !important;
}
.v-simple-table >>> td {
  border-bottom: 1px solid #f1f5f9 !important;
}
</style>
